// Parsing for the date strings kraken sends us.
//
// kraken formats every date through `Timezone::asLocalTimezone` (kraken
// `app/Models/Timezone.php:41`), so what arrives here has already been converted to the user's
// local timezone - but in one of four shapes, depending on which resource served the field:
//
//   'M d, Y, H:i'   'Aug 04, 2026, 14:30'       the default when no format is passed -
//                                               PrescriptionResource, ChartPrescriptionResource,
//                                               PatientAdminChartResource, ReadingResource
//   'd/m/Y'         '04/08/2026'                ChartResource, ChartEditResource
//   'Y-m-d'         '2026-08-04'                ErxChartResource (so every erx_chart
//                                               start_date/end_date), MedicationListResource
//   ISO 8601        '2026-08-04T00:00:00.000Z'  an unformatted Carbon instance - PRNResource
//
// The same logical field therefore reaches the frontend in different shapes on different
// screens: a prescription's `stop_date` is 'd/m/Y' from the chart endpoints and 'M d, Y, H:i'
// from the administration ones. Every caller has to accept all four, which is why this parsing
// lives in one place rather than being re-sniffed per component.

const DAY_MS = 24 * 60 * 60 * 1000;
const DMY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const ISO_DATE_ONLY = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

function validated(date) {
    return isNaN(date.getTime()) ? null : date;
}

// The instant a kraken date string refers to, or null if it isn't a date we can read.
// Time-of-day is preserved - use parseChartDate() for whole-day questions.
export function parseKrakenDate(value) {
    if (!value) return null;
    if (value instanceof Date) return validated(new Date(value.getTime()));
    if (typeof value !== 'string') return null;

    const trimmed = value.trim();

    const dmy = DMY.exec(trimmed);
    if (dmy) {
        return validated(new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1])));
    }

    // 'Y-m-d' is deliberately not handed to `new Date()`. A bare date string is parsed as UTC
    // midnight per the ECMAScript spec, which resolves to the *previous* calendar day at any
    // negative UTC offset. kraken has already shifted these into the user's local timezone, so
    // the components are local ones and are built as such. Australian offsets are all positive,
    // so this is currently a latent rather than live defect - but it is the shape erx_chart
    // dates always arrive in, so it is not one to leave resting on the timezone map.
    const iso = ISO_DATE_ONLY.exec(trimmed);
    if (iso) {
        return validated(new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
    }

    // 'M d, Y, H:i' and full ISO 8601 both parse natively, and both carry a time-of-day.
    return validated(new Date(trimmed));
}

// Local midnight on the day a chart/prescription date falls on, or null if unreadable.
// Normalised because every caller is asking a whole-day question ("expired?", "how many days
// left?") about a field that only sometimes carries a time-of-day.
export function parseChartDate(value) {
    const date = parseKrakenDate(value);
    if (!date) return null;
    date.setHours(0, 0, 0, 0);
    return date;
}

export function startOfToday() {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
}

// Whether a date has already passed. A date that expires *today* has not.
// An unreadable date is not expired - the callers all prefer showing a medication to hiding it
// on the strength of a field they couldn't parse.
export function isBeforeToday(value) {
    const date = parseChartDate(value);
    return date ? date < startOfToday() : false;
}

// Whole days from today: 0 = falls today, negative = already past, null = unreadable.
// Rounded rather than ceiled: both operands are local midnights, so a daylight-saving
// transition inside the interval makes it 23 or 25 hours long, and ceil() turns the 25-hour
// case into an extra day.
export function daysUntil(value) {
    const date = parseChartDate(value);
    return date === null ? null : Math.round((date - startOfToday()) / DAY_MS);
}

// Largest-unit-first, mirroring Carbon's diffForHumans() thresholds: days up to a week, then
// weeks up to a month, then months up to a year.
const DIVISIONS = [
    { unit: 'second', seconds: 1, until: 60 },
    { unit: 'minute', seconds: 60, until: 60 * 60 },
    { unit: 'hour', seconds: 60 * 60, until: 24 * 60 * 60 },
    { unit: 'day', seconds: 24 * 60 * 60, until: 7 * 24 * 60 * 60 },
    { unit: 'week', seconds: 7 * 24 * 60 * 60, until: 30 * 24 * 60 * 60 },
    { unit: 'month', seconds: 30 * 24 * 60 * 60, until: 365 * 24 * 60 * 60 },
    { unit: 'year', seconds: 365 * 24 * 60 * 60, until: Infinity },
];

// A relative distance in words in the style of Carbon's diffForHumans() - '3 days ago',
// '2 weeks ago', 'in 5 hours'. Returns null when the value isn't a date we can read, so callers
// can fall back to the absolute timestamp rather than rendering 'Invalid Date ago'.
//
// Months and years are approximated at 30 and 365 days instead of being walked calendar by
// calendar. This is display text for how long ago something was recorded, where the difference
// between '11 months ago' and '1 year ago' carries no weight.
export function diffForHumans(value, now) {
    const date = parseKrakenDate(value);
    if (date === null) return null;

    const reference = now instanceof Date ? now : new Date();
    const seconds = Math.round((reference - date) / 1000);
    const elapsed = Math.abs(seconds);

    const division = DIVISIONS.find(entry => elapsed < entry.until);
    // Anything under a second reads as '1 second', matching Carbon rather than saying '0'.
    const count = Math.max(1, Math.floor(elapsed / division.seconds));
    const phrase = `${count} ${division.unit}${count === 1 ? '' : 's'}`;

    return seconds < 0 ? `in ${phrase}` : `${phrase} ago`;
}
